/**
 * Unlock flow. Called on every app launch (and when the in-memory
 * session expires). Returns a decrypted bearer token + user summary,
 * or throws on failure.
 *
 * After 5 consecutive PIN failures, the device record is wiped and
 * the user must re-pair.
 */

import type {
  AuthHttp,
  DeviceRecord,
  DeviceStore,
  UnlockedSession,
} from './types.js';
import {
  base64urlDecode,
  base64urlEncode,
  deriveKeyFromPin,
  deriveKeyFromPrf,
  unwrapToken,
} from './crypto.js';

const MAX_PIN_ATTEMPTS = 5;

export class TooManyPinAttemptsError extends Error {
  readonly code = 'TOO_MANY_PIN_ATTEMPTS';
  constructor() {
    super('Too many failed PIN attempts. Device wiped — please re-pair.');
  }
}

export class WrongPinError extends Error {
  readonly code = 'WRONG_PIN';
  constructor(public attemptsRemaining: number) {
    super(`Wrong PIN — ${attemptsRemaining} attempt(s) remaining.`);
  }
}

export class WebAuthnFailedError extends Error {
  readonly code = 'WEBAUTHN_FAILED';
  constructor(message: string) {
    super(message);
  }
}

export class NoDeviceError extends Error {
  readonly code = 'NO_DEVICE';
  constructor() {
    super('No paired device on this browser. Pair first.');
  }
}

export interface UnlockOptions {
  store: DeviceStore;
  http: AuthHttp;
  rpId: string;
  /** PIN required when device.unlock_method === 'pin'. Ignored otherwise. */
  pin?: string;
}

export async function unlock(opts: UnlockOptions): Promise<UnlockedSession> {
  const device = await opts.store.load();
  if (!device) throw new NoDeviceError();

  if (device.unlock_method === 'webauthn' && device.credential_id) {
    return unlockWebAuthn(opts, device);
  }
  if (!opts.pin) {
    throw new Error('PIN required to unlock this device');
  }
  return unlockPin(opts, device, opts.pin);
}

async function unlockPin(
  opts: UnlockOptions,
  device: DeviceRecord,
  pin: string,
): Promise<UnlockedSession> {
  // Refresh the salt from the server. This is the rate-limit choke point
  // that defeats offline brute force — without an online salt fetch the
  // PIN can't be verified at all.
  const saltResp = await opts.http.postJson<
    { device_id: string },
    { salt: string }
  >('/api/v1/auth/device/unlock-salt', { device_id: device.device_id });
  const salt = base64urlDecode(saltResp.salt);

  const key = await deriveKeyFromPin(pin, salt);
  let token: string;
  try {
    token = await unwrapToken(key, device.iv, device.wrapped_token);
  } catch {
    const attempts = await opts.store.incrementPinAttempts();
    if (attempts >= MAX_PIN_ATTEMPTS) {
      await opts.store.clear();
      throw new TooManyPinAttemptsError();
    }
    throw new WrongPinError(MAX_PIN_ATTEMPTS - attempts);
  }
  await opts.store.resetPinAttempts();
  return {
    device_id: device.device_id,
    user: device.user,
    bearer_token: token,
    expires_at: device.expires_at,
  };
}

async function unlockWebAuthn(
  opts: UnlockOptions,
  device: DeviceRecord,
): Promise<UnlockedSession> {
  if (!device.credential_id || !device.prf_supported) {
    throw new WebAuthnFailedError(
      'Device was paired without PRF support — use PIN instead.',
    );
  }

  // Server challenge.
  const challengeResp = await opts.http.postJson<
    { device_id: string },
    { challenge: string }
  >('/api/v1/auth/device/webauthn/auth/options', { device_id: device.device_id });
  const challenge = base64urlDecode(challengeResp.challenge);

  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      publicKey: {
        challenge: challenge as unknown as BufferSource,
        rpId: opts.rpId,
        allowCredentials: [
          {
            type: 'public-key',
            id: device.credential_id as unknown as BufferSource,
          },
        ],
        userVerification: 'required',
        timeout: 60_000,
        extensions: {
          prf: { eval: { first: new Uint8Array([1]) } },
        } as Record<string, unknown>,
      },
    })) as PublicKeyCredential | null;
  } catch (err) {
    throw new WebAuthnFailedError((err as Error).message);
  }

  if (!assertion) throw new WebAuthnFailedError('User cancelled biometric prompt');

  const extensionResults = (assertion as unknown as {
    getClientExtensionResults?: () => Record<string, unknown>;
  }).getClientExtensionResults?.() as
    | { prf?: { results?: { first?: ArrayBuffer } } }
    | undefined;

  const prfFirst = extensionResults?.prf?.results?.first;
  if (!prfFirst) {
    throw new WebAuthnFailedError('Authenticator did not return PRF output');
  }

  // Verify the assertion server-side (proof of possession + replay protection).
  const assertionResponse = assertion.response as AuthenticatorAssertionResponse;
  await opts.http.postJson<unknown, { ok: true }>(
    '/api/v1/auth/device/webauthn/auth/verify',
    {
      device_id: device.device_id,
      credential_id: base64urlEncode(new Uint8Array(assertion.rawId)),
      authenticator_data: base64urlEncode(new Uint8Array(assertionResponse.authenticatorData)),
      client_data_json: base64urlEncode(new Uint8Array(assertionResponse.clientDataJSON)),
      signature: base64urlEncode(new Uint8Array(assertionResponse.signature)),
    },
  );

  const key = await deriveKeyFromPrf(prfFirst);
  const token = await unwrapToken(key, device.iv, device.wrapped_token);

  return {
    device_id: device.device_id,
    user: device.user,
    bearer_token: token,
    expires_at: device.expires_at,
  };
}

/**
 * Refresh the device token if it's within `daysBeforeExpiry` of expiring.
 * Returns the (possibly new) bearer token; updates the wrapped token in
 * the store transparently. Caller must already be unlocked.
 */
export async function maybeRefreshToken(
  session: UnlockedSession,
  http: AuthHttp,
  daysBeforeExpiry = 7,
): Promise<UnlockedSession> {
  const expiresMs = Date.parse(session.expires_at);
  const cutoff = Date.now() + daysBeforeExpiry * 24 * 3600 * 1000;
  if (expiresMs > cutoff) return session;

  const refreshed = await http.postJson<
    { device_id: string; current_token: string },
    { device_token: string; expires_at: string }
  >('/api/v1/auth/device/refresh', {
    device_id: session.device_id,
    current_token: session.bearer_token,
  });
  return {
    ...session,
    bearer_token: refreshed.device_token,
    expires_at: refreshed.expires_at,
  };
}
